/**
 * Minimal, dependency-free Markdown → HTML renderer ported from the Beamhop design.
 * Supports headings, fenced code, tables, blockquotes, lists, hr, and inline
 * bold/italic/code/links/images. Unlike the prototype it sanitizes URL schemes so
 * rendering other people's notes can't inject `javascript:` payloads.
 */

export type TocEntry = { id: string; text: string; level: number };

const esc = (s: string): string =>
  s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

const SAFE_SCHEME = /^(https?:|mailto:|nostr:|#|\/)/i;

const safeUrl = (raw: string): string => {
  const url = raw.trim();
  return SAFE_SCHEME.test(url) ? url : "#";
};

const slugify = (text: string): string =>
  text
    .toLowerCase()
    .replace(/<[^>]+>/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "") || "section";

const inline = (raw: string): string => {
  const codes: string[] = [];
  let t = raw.replace(/`([^`]+)`/g, (_m, c: string) => {
    codes.push(c);
    return ` ${codes.length - 1} `;
  });
  t = esc(t);
  t = t.replace(
    /!\[([^\]]*)\]\(\s*([^)\s]+?)(?:\s+&quot;([^&]*)&quot;|\s+"([^"]*)")?\s*\)/g,
    (_m, alt: string, src: string, t1?: string, t2?: string) => {
      const ti = t1 ?? t2;
      return `<img alt="${alt}" src="${safeUrl(src)}"${ti ? ` title="${ti}"` : ""}>`;
    },
  );
  t = t.replace(
    /\[([^\]]+)\]\(\s*([^)\s]+?)(?:\s+&quot;([^&]*)&quot;|\s+"([^"]*)")?\s*\)/g,
    (_m, txt: string, href: string, t1?: string, t2?: string) => {
      const ti = t1 ?? t2;
      return `<a href="${safeUrl(href)}" target="_blank" rel="noopener noreferrer"${ti ? ` title="${ti}"` : ""}>${txt}</a>`;
    },
  );
  t = t.replace(/\*\*([^*]+?)\*\*/g, "<strong>$1</strong>");
  t = t.replace(/__([^_]+?)__/g, "<strong>$1</strong>");
  t = t.replace(/(^|[^*])\*([^*\s][^*]*?)\*/g, "$1<em>$2</em>");
  t = t.replace(/(^|[^_\w])_([^_\s][^_]*?)_/g, "$1<em>$2</em>");
  t = t.replace(/ (\d+) /g, (_m, n: string) => `<code>${esc(codes[Number(n)] ?? "")}</code>`);
  return t;
};

const isRow = (l: string): boolean => /^\s*\|.*\|\s*$/.test(l);
const cells = (l: string): string[] =>
  l
    .replace(/^\s*\|/, "")
    .replace(/\|\s*$/, "")
    .split("|")
    .map((c) => c.trim());

export type RenderResult = { html: string; toc: TocEntry[] };

export const renderMarkdown = (src: string): RenderResult => {
  if (!src) return { html: "", toc: [] };
  const toc: TocEntry[] = [];
  const lines = src.replace(/\r\n/g, "\n").split("\n");
  let html = "";
  let i = 0;
  while (i < lines.length) {
    const l = lines[i] ?? "";
    if (/^\s*$/.test(l)) {
      i++;
      continue;
    }
    const fence = l.match(/^\s*```(.*)$/);
    if (fence) {
      i++;
      const buf: string[] = [];
      while (i < lines.length && !/^\s*```\s*$/.test(lines[i] ?? "")) {
        buf.push(lines[i] ?? "");
        i++;
      }
      i++;
      html += `<pre><code>${esc(buf.join("\n"))}</code></pre>`;
      continue;
    }
    const h = l.match(/^(#{1,6})\s+(.*)$/);
    if (h) {
      const n = (h[1] ?? "").length;
      const text = inline((h[2] ?? "").trim());
      const id = slugify(h[2] ?? "");
      if (n <= 3) toc.push({ id, text: (h[2] ?? "").trim(), level: n });
      html += `<h${n} id="${id}">${text}</h${n}>`;
      i++;
      continue;
    }
    if (/^\s*([-*_])(\s*\1){2,}\s*$/.test(l)) {
      html += "<hr>";
      i++;
      continue;
    }
    if (isRow(l) && i + 1 < lines.length && /^\s*\|?[\s:|-]+\|?\s*$/.test(lines[i + 1] ?? "") && (lines[i + 1] ?? "").includes("-")) {
      const head = cells(l);
      i += 2;
      const rows: string[][] = [];
      while (i < lines.length && isRow(lines[i] ?? "")) {
        rows.push(cells(lines[i] ?? ""));
        i++;
      }
      html += `<table><thead><tr>${head.map((c) => `<th>${inline(c)}</th>`).join("")}</tr></thead><tbody>${rows
        .map((r) => `<tr>${r.map((c) => `<td>${inline(c)}</td>`).join("")}</tr>`)
        .join("")}</tbody></table>`;
      continue;
    }
    if (/^\s*>\s?/.test(l)) {
      const buf: string[] = [];
      while (i < lines.length && /^\s*>\s?/.test(lines[i] ?? "")) {
        buf.push((lines[i] ?? "").replace(/^\s*>\s?/, ""));
        i++;
      }
      html += `<blockquote>${buf.map((b) => `<p>${inline(b)}</p>`).join("")}</blockquote>`;
      continue;
    }
    if (/^\s*[-*+]\s+/.test(l)) {
      const buf: string[] = [];
      while (i < lines.length && /^\s*[-*+]\s+/.test(lines[i] ?? "")) {
        buf.push((lines[i] ?? "").replace(/^\s*[-*+]\s+/, ""));
        i++;
      }
      html += `<ul>${buf.map((b) => `<li>${inline(b)}</li>`).join("")}</ul>`;
      continue;
    }
    if (/^\s*\d+\.\s+/.test(l)) {
      const buf: string[] = [];
      while (i < lines.length && /^\s*\d+\.\s+/.test(lines[i] ?? "")) {
        buf.push((lines[i] ?? "").replace(/^\s*\d+\.\s+/, ""));
        i++;
      }
      html += `<ol>${buf.map((b) => `<li>${inline(b)}</li>`).join("")}</ol>`;
      continue;
    }
    const buf: string[] = [];
    while (
      i < lines.length &&
      !/^\s*$/.test(lines[i] ?? "") &&
      !/^\s*(#{1,6}\s|```|>|[-*+]\s|\d+\.\s)/.test(lines[i] ?? "") &&
      !/^\s*([-*_])(\s*\1){2,}\s*$/.test(lines[i] ?? "")
    ) {
      buf.push(lines[i] ?? "");
      i++;
    }
    if (buf.length) html += `<p>${buf.map((b) => inline(b)).join("<br>")}</p>`;
  }
  return { html, toc };
};

export const countWords = (t: string): number => (t.trim().match(/\S+/g) ?? []).length;
export const readingMinutes = (words: number): number => Math.max(1, Math.round(words / 200));
